import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { GBP, ukDateLong, STATUS_LABELS } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Users, PoundSterling, UserPlus, CalendarX, CalendarClock } from "lucide-react";

function StatCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 text-2xl font-bold font-mono">{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-stone-500">Loading…</div>;

  return (
    <div className="max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-extrabold text-3xl">Dashboard</h1>
          <p className="text-stone-600 text-sm mt-1">{ukDateLong(new Date())}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-revenue" icon={PoundSterling} label="Today's revenue" value={GBP(data.today_revenue)} tone="bg-emerald-50 text-emerald-700" />
        <StatCard testid="stat-appointments" icon={CalendarClock} label="Appointments today" value={data.today_appointments.length} tone="bg-sky-50 text-sky-700" />
        <StatCard testid="stat-clients" icon={Users} label="Total clients" value={data.client_count} tone="bg-[#F3EFEA] text-[#B07E4F]" />
        <StatCard testid="stat-new-clients" icon={UserPlus} label="New clients (7d)" value={data.new_clients} tone="bg-violet-50 text-violet-700" />
        <StatCard testid="stat-no-shows" icon={CalendarX} label="No-shows (7d)" value={data.no_shows} tone="bg-rose-50 text-rose-700" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="font-semibold text-lg">Today's appointments</h2>
            <button onClick={() => navigate("/calendar")} className="text-sm text-[#B07E4F] font-medium">Open calendar →</button>
          </div>
          <div className="p-3">
            {data.today_appointments.length === 0 ? (
              <p className="text-sm text-stone-500 p-4">No appointments today.</p>
            ) : data.today_appointments.map((a) => (
              <div key={a.id} data-testid={`today-appt-${a.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50">
                <span className="font-mono text-sm font-medium w-14">{a.start}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{a.client_name}</div>
                  <div className="text-xs text-stone-500 truncate">{a.service_name} · {a.staff_name}</div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
          <div className="px-5 py-4 border-b border-stone-100"><h2 className="font-semibold text-lg">Upcoming</h2></div>
          <div className="p-3">
            {data.upcoming_appointments.length === 0 ? (
              <p className="text-sm text-stone-500 p-4">No upcoming appointments.</p>
            ) : data.upcoming_appointments.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50">
                <div className="text-center w-14">
                  <div className="text-[11px] text-stone-500">{ukDateLong(a.date).split(",")[0]}</div>
                  <div className="font-mono text-sm font-medium">{a.start}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{a.client_name}</div>
                  <div className="text-xs text-stone-500 truncate">{a.service_name} · {a.staff_name}</div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
